import type { SellerAuthView } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";
import { canAccess, type AccessRule } from "./store-access";

/**
 * The settings sections a Core (Free) store shows, in order, with their
 * labels. ONE list for the two places that name them — the chips on
 * `CoreStoreSettingsPage` and the "إعدادات المتجر" sub-menu in the sidebar —
 * which used to disagree ("الهوية / الفريق والصلاحيات" in the sidebar,
 * "شعار المتجر / الفريق" on the page). Free keeps the plain set.
 *
 * A store is Core when it lacks `profileBranding` (the switch
 * `StoreSettingsPage` uses to pick the page).
 */
export type CoreSettingsSectionId =
  | "branding"
  | "payment"
  | "shipping"
  | "domain"
  | "team"
  | "marketing"
  | "help";

type SectionDef = {
  id: CoreSettingsSectionId;
  labelKey: string;
  rule: AccessRule | null;
  /** Shown only where the ads feature exists at all. */
  ads?: boolean;
};

const c = keys.seller.coreStore;

const CORE_SECTIONS: readonly SectionDef[] = [
  { id: "branding", labelKey: c.logo, rule: { capability: "logo", permission: "STORE_VIEW" } },
  { id: "payment", labelKey: c.payment, rule: { capability: "storeSettings", permission: "STORE_VIEW" } },
  { id: "shipping", labelKey: c.shipping, rule: { capability: "shippingFees", permission: "SHIPPING_VIEW" } },
  { id: "domain", labelKey: c.domain, rule: { capability: "storeSettings", permission: "STORE_VIEW" } },
  // Withdraw-only on Free: see who has access and take it back. Adding
  // people stays Premium (backend: list/remove/revoke are `storeSettings`).
  { id: "team", labelKey: keys.coreSettings.teamTitle, rule: { capability: "storeSettings", permission: "MEMBER_VIEW" } },
  // Withdraw-only too (Meta CAPI status + disconnect).
  { id: "marketing", labelKey: keys.coreSettings.marketingSection, rule: { capability: "storeSettings", permission: "STORE_VIEW" }, ads: true },
  { id: "help", labelKey: c.help, rule: null },
];

export function isCoreSettingsStore(seller: SellerAuthView | undefined): boolean {
  return seller !== undefined && !canAccess(seller, { capability: "profileBranding" });
}

export function coreSettingsSections(
  seller: SellerAuthView | undefined,
  { adsEnabled }: { adsEnabled: boolean },
): { id: CoreSettingsSectionId; labelKey: string }[] {
  return CORE_SECTIONS.filter(
    (s) => (!s.ads || adsEnabled) && (s.rule === null || canAccess(seller, s.rule)),
  ).map(({ id, labelKey }) => ({ id, labelKey }));
}
